import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { adminBusinesses, adminCreateBusiness, adminStats, resetOwnerPassword, setBusinessStatus, setBusinessModules, setBusinessWallet } from "@/api";
import { useAuth } from "@/AuthContext";
import { PlatformLayout, Loading } from "./Layout";
import { LoyaltyBrandingFields, DEFAULT_CONFIG, splitConfig } from "./LoyaltyBrandingFields";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Store, Users, Stamp, Gift, Plus, Loader2, X, ExternalLink, UserRound, ShieldCheck, KeyRound, Wallet,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { toast } from "sonner";

function formatErr(detail) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || JSON.stringify(e)).join(" ");
  return "Etwas ist schiefgelaufen.";
}

export const AdminConsole = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    const [s, b] = await Promise.all([adminStats(), adminBusinesses()]);
    setStats(s); setBusinesses(b); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleLogout = async () => { await logout(); navigate("/login"); };

  const enterSupport = (b) => {
    localStorage.setItem("bs_support_bid", b.id);
    navigate("/support");
  };
  const supportBid = (() => { try { return localStorage.getItem("bs_support_bid"); } catch (e) { return null; } })();
  const [pwTarget, setPwTarget] = useState(null);
  const toggleBiz = async (b) => {
    try {
      await setBusinessStatus(b.id, !b.active);
      toast.success(b.active ? "Betrieb deaktiviert" : "Betrieb aktiviert");
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const toggleModule = async (b, key) => {
    const current = b.modules?.[key] ?? (key === "loyalty");
    try {
      await setBusinessModules(b.id, { [key]: !current });
      toast.success("Module aktualisiert ✓");
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const toggleWallet = async (b, key) => {
    const w = b.wallet || {};
    // Provider toggles only make sense when Wallet is globally enabled.
    if ((key === "apple_enabled" || key === "google_enabled") && !w.enabled) return;
    try {
      await setBusinessWallet(b.id, { [key]: !w[key] });
      toast.success("Wallet aktualisiert ✓");
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  return (
    <PlatformLayout user={{}} logout={handleLogout} subtitle="Plattform-Konsole">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display font-extrabold text-2xl text-[var(--bs-ink)]">Plattform-Übersicht</h1>
          <p className="text-slate-500 text-sm font-medium">Alle Betriebe auf Bonstelo verwalten.</p>
        </div>
        <Button data-testid="admin-new-business-button" onClick={() => setOpen(true)}
          className="rounded-2xl bg-[var(--bs-teal)] hover:bg-[var(--bs-teal-hover)] font-display font-bold press">
          <Plus className="mr-1 h-5 w-5" /> Betrieb anlegen
        </Button>
      </div>

      {loading ? <Loading /> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <StatCard testid="admin-stat-businesses" icon={Store} value={stats.businesses} label="Betriebe" />
            <StatCard testid="admin-stat-customers" icon={UserRound} value={stats.customers} label="Kunden gesamt" />
            <StatCard testid="admin-stat-stamps" icon={Stamp} value={stats.stamps} label="Buchungen" />
            <StatCard testid="admin-stat-rewards" icon={Gift} value={stats.rewards_redeemed} label="Belohnungen eingelöst" />
          </div>

          <div className="bg-white rounded-3xl ring-1 ring-slate-100 shadow-soft overflow-hidden">
            <div className="px-5 py-3.5 border-b border-slate-100 flex items-center gap-2">
              <Users className="h-4 w-4 text-slate-400" />
              <h2 className="font-display font-extrabold text-sm text-slate-700">Betriebe ({businesses.length})</h2>
            </div>
            {businesses.length === 0 ? (
              <p className="text-center text-slate-400 text-sm p-8">Noch keine Betriebe. Lege den ersten an.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {businesses.map((b) => (
                  <div key={b.id} data-testid="admin-business-row" className="px-5 py-4 space-y-3">
                    {/* Row 1: identity + status */}
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-xl flex items-center justify-center text-white font-display font-extrabold shrink-0" style={{ background: b.primary_color }}>{b.name[0]}</div>
                      <div className="flex-1 min-w-0">
                        <p className="font-display font-bold text-slate-900 text-sm truncate">{b.name}</p>
                        <p className="text-xs text-slate-400 truncate">{b.owner_email} · {b.customers} Kunden · {b.staff} Team</p>
                      </div>
                      <span className="hidden sm:inline text-[11px] font-bold text-slate-500 bg-slate-100 rounded-full px-2.5 py-1 whitespace-nowrap">
                        {b.loyalty_mode === "points_spend" ? "Punkte" : b.loyalty_mode === "stamp_spend" ? "Umsatz-Stempel" : "Stempel"}
                      </span>
                      <span data-testid="admin-business-status" className={`text-[11px] font-bold rounded-full px-2.5 py-1 whitespace-nowrap ${b.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>{b.active ? "Aktiv" : "Inaktiv"}</span>
                      {supportBid === b.id && (
                        <span data-testid="admin-support-active-badge" className="inline-flex items-center gap-1 text-[11px] font-extrabold rounded-full px-2.5 py-1 bg-[var(--bs-teal)] text-white whitespace-nowrap">
                          <ShieldCheck className="h-3.5 w-3.5" /> Support-Modus aktiv
                        </span>
                      )}
                    </div>

                    {/* Row 2: module + wallet toggles (wrap freely) */}
                    <div className="flex flex-wrap items-center gap-2" title="Module & Wallet verwalten">
                      <span data-testid="admin-module-loyalty" className="text-[11px] font-bold rounded-full px-2.5 py-1 bg-emerald-100 text-emerald-700 whitespace-nowrap">Loyalty · Aktiv</span>
                      {(() => {
                        const on = b.modules?.feedback ?? false;
                        return (
                          <button data-testid="admin-module-feedback" data-on={on} onClick={() => toggleModule(b, "feedback")}
                            className={`text-[11px] font-bold rounded-full px-2.5 py-1 press whitespace-nowrap ring-1 ${on ? "bg-[var(--bs-teal)] text-white ring-transparent" : "bg-white text-slate-400 ring-slate-200 hover:bg-slate-50"}`}>
                            Feedback {on ? "Ein" : "Aus"}
                          </button>
                        );
                      })()}
                      {(() => {
                        const on = b.modules?.menu ?? false;
                        return (
                          <button data-testid="admin-module-menu" data-on={on} onClick={() => toggleModule(b, "menu")}
                            className={`text-[11px] font-bold rounded-full px-2.5 py-1 press whitespace-nowrap ring-1 ${on ? "bg-[var(--bs-teal)] text-white ring-transparent" : "bg-white text-slate-400 ring-slate-200 hover:bg-slate-50"}`}>
                            Menükarte {on ? "Ein" : "Aus"}
                          </button>
                        );
                      })()}
                      {(() => {
                        const w = b.wallet || {};
                        const walletOn = !!w.enabled;
                        return (
                          <>
                            <span className="mx-1 h-4 w-px bg-slate-200 hidden sm:inline-block" aria-hidden="true" />
                            <button data-testid="admin-wallet-enabled" data-on={walletOn} onClick={() => toggleWallet(b, "enabled")}
                              title="Wallet gehört zum Loyalty-Bereich"
                              className={`inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2.5 py-1 press whitespace-nowrap ring-1 ${walletOn ? "bg-[var(--bs-teal)] text-white ring-transparent" : "bg-white text-slate-400 ring-slate-200 hover:bg-slate-50"}`}>
                              <Wallet className="h-3 w-3" /> Wallet {walletOn ? "Ein" : "Aus"}
                            </button>
                            <button data-testid="admin-wallet-apple" data-on={!!w.apple_enabled} disabled={!walletOn} onClick={() => toggleWallet(b, "apple_enabled")}
                              className={`text-[11px] font-bold rounded-full px-2.5 py-1 press whitespace-nowrap ring-1 ${!walletOn ? "bg-slate-50 text-slate-300 ring-slate-100 cursor-not-allowed" : w.apple_enabled ? "bg-slate-900 text-white ring-transparent" : "bg-white text-slate-400 ring-slate-200 hover:bg-slate-50"}`}>
                              Apple {w.apple_enabled ? "Ein" : "Aus"}
                            </button>
                            <button data-testid="admin-wallet-google" data-on={!!w.google_enabled} disabled={!walletOn} onClick={() => toggleWallet(b, "google_enabled")}
                              className={`text-[11px] font-bold rounded-full px-2.5 py-1 press whitespace-nowrap ring-1 ${!walletOn ? "bg-slate-50 text-slate-300 ring-slate-100 cursor-not-allowed" : w.google_enabled ? "bg-[#4285F4] text-white ring-transparent" : "bg-white text-slate-400 ring-slate-200 hover:bg-slate-50"}`}>
                              Google {w.google_enabled ? "Ein" : "Aus"}
                            </button>
                          </>
                        );
                      })()}
                    </div>

                    {/* Row 3: actions */}
                    <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-slate-100">
                      <button data-testid="admin-toggle-status" onClick={() => toggleBiz(b)}
                        className={`px-3 py-2 rounded-xl text-xs font-bold press whitespace-nowrap ${b.active ? "bg-amber-100 text-amber-800 hover:bg-amber-200" : "bg-emerald-600 text-white hover:bg-emerald-700"}`}>
                        {b.active ? "Deaktivieren" : "Aktivieren"}
                      </button>
                      <button data-testid="admin-support-button" onClick={() => enterSupport(b)}
                        className={`flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold press whitespace-nowrap ${supportBid === b.id ? "bg-[var(--bs-teal)] text-white ring-2 ring-[var(--bs-teal)]/40" : "bg-[var(--bs-teal)]/10 text-[var(--bs-teal)] hover:bg-[var(--bs-teal)]/20"}`}>
                        <ShieldCheck className="h-4 w-4" /> {supportBid === b.id ? "Support aktiv" : "Support-Modus"}
                      </button>
                      <div className="flex-1 min-w-[8px]" />
                      <button data-testid="admin-reset-owner-pw" title="Inhaber-Passwort zurücksetzen"
                        onClick={() => setPwTarget(b)} className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 press"><KeyRound className="h-4 w-4" /></button>
                      <button data-testid="admin-open-poster" title="QR-Aushang" onClick={() => window.open(`/poster/${b.slug}`, "_blank")} className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 press"><ExternalLink className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <NewBusinessDialog open={open} onClose={() => setOpen(false)} onCreated={() => { setOpen(false); load(); }} />
      <OwnerPwDialog target={pwTarget} onClose={() => setPwTarget(null)} />
    </PlatformLayout>
  );
};

const OwnerPwDialog = ({ target, onClose }) => {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await resetOwnerPassword(target.id, pw);
      toast.success("Inhaber-Passwort zurückgesetzt ✓");
      setPw(""); onClose();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Inhaber-Passwort zurücksetzen</DialogTitle>
          <DialogDescription>{target ? `Neues Passwort für den Inhaber von „${target.name}". Das bestehende Passwort wird nie angezeigt.` : ""}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Input data-testid="admin-owner-pw-input" type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Neues Passwort (min. 6)" className="h-11 rounded-xl" required minLength={6} />
          <Button data-testid="admin-owner-pw-submit" type="submit" disabled={busy}
            className="w-full py-3 rounded-2xl bg-[var(--bs-teal)] hover:bg-[var(--bs-teal-hover)] font-display font-bold press disabled:opacity-60">
            {busy ? <Loader2 className="mr-1 h-5 w-5 animate-spin" /> : <KeyRound className="mr-1 h-5 w-5" />} Passwort setzen
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const StatCard = ({ testid, icon: Icon, value, label }) => (
  <div data-testid={testid} className="bg-white rounded-2xl ring-1 ring-slate-100 shadow-soft p-4">
    <Icon className="h-5 w-5 text-[var(--bs-teal)] mb-2" />
    <p className="font-display font-extrabold text-2xl text-slate-900 leading-none">{value}</p>
    <p className="text-[11px] font-semibold text-slate-400 mt-1">{label}</p>
  </div>
);

const NewBusinessDialog = ({ open, onClose, onCreated }) => {
  const [biz, setBiz] = useState("");
  const [owner, setOwner] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [cfg, setCfg] = useState({ ...DEFAULT_CONFIG });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { branding, loyalty } = splitConfig(cfg);
      await adminCreateBusiness({
        business_name: biz.trim(), owner_name: owner.trim(), owner_email: email.trim(),
        owner_password: password, branding, loyalty,
      });
      toast.success("Betrieb angelegt ✓");
      setBiz(""); setOwner(""); setEmail(""); setPassword(""); setCfg({ ...DEFAULT_CONFIG });
      onCreated();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Neuen Betrieb anlegen</DialogTitle>
          <DialogDescription>Erstellt den Betrieb inkl. Inhaber-Zugang.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 gap-3">
            <Input data-testid="admin-biz-name-input" value={biz} onChange={(e) => setBiz(e.target.value)} placeholder="Name des Betriebs" className="h-11 rounded-xl" required />
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="admin-owner-name-input" value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="Inhaber-Name" className="h-11 rounded-xl" required />
              <Input data-testid="admin-owner-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Inhaber-E-Mail" className="h-11 rounded-xl" required />
            </div>
            <Input data-testid="admin-owner-password-input" type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Inhaber-Passwort (min. 6)" className="h-11 rounded-xl" required minLength={6} />
          </div>
          <LoyaltyBrandingFields value={cfg} onChange={setCfg} idPrefix="admin" />
          <Button data-testid="admin-create-business-submit" type="submit" disabled={busy}
            className="w-full py-3.5 rounded-2xl bg-[var(--bs-teal)] hover:bg-[var(--bs-teal-hover)] font-display font-bold press disabled:opacity-60">
            {busy ? <><Loader2 className="mr-1 h-5 w-5 animate-spin" /> Anlegen…</> : "Betrieb anlegen"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
